'use client'

import { useState, useEffect, useCallback } from 'react'
import { BsTicketPerforated, BsCupStraw, BsCameraReels, BsBalloon, BsBuilding, BsChevronRight, BsX, BsArrowLeft } from 'react-icons/bs'

interface LocationOption {
  id: string
  name: string
  type: string
  hasChildren: boolean
}

interface BestOfWizardProps {
  initialLocations: LocationOption[]
  onFilterChange: (filters: { type?: string; locationId?: string }) => void
}

const ENTITY_TYPES = [
  { id: 'ATTRACTION', name: 'Attractions', icon: BsTicketPerforated, slug: 'attractions' },
  { id: 'RESTAURANT', name: 'Restaurants', icon: BsCupStraw, slug: 'restaurants' },
  { id: 'SHOW', name: 'Shows', icon: BsCameraReels, slug: 'shows' },
  { id: 'ENTERTAINMENT', name: 'Entertainment', icon: BsBalloon, slug: 'entertainment' },
  { id: 'RESORT', name: 'Resorts', icon: BsBuilding, slug: 'resorts' },
]

interface BreadcrumbItem {
  id: string
  name: string
}

export function BestOfWizard({ initialLocations, onFilterChange }: BestOfWizardProps) {
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [locationPath, setLocationPath] = useState<BreadcrumbItem[]>([])
  const [currentLocations, setCurrentLocations] = useState<LocationOption[]>(initialLocations)
  const [loadingLocations, setLoadingLocations] = useState(false)
  const [showingResults, setShowingResults] = useState(false)

  // Fetch children of a location
  const fetchChildren = useCallback(async (parentId: string) => {
    setLoadingLocations(true)
    try {
      const response = await fetch(`/api/locations/${parentId}/children`)
      const data = await response.json()
      return data.children || []
    } catch (error) {
      console.error('Failed to fetch children:', error)
      return []
    } finally {
      setLoadingLocations(false)
    }
  }, [])

  // Notify parent of filter changes
  useEffect(() => {
    const lastLocation = locationPath[locationPath.length - 1]
    onFilterChange({
      type: selectedType || undefined,
      locationId: lastLocation?.id || undefined,
    })
  }, [selectedType, locationPath, onFilterChange])

  const handleTypeSelect = async (typeId: string) => {
    setSelectedType(typeId)
    setLocationPath([])
    setShowingResults(false)

    // Load appropriate top-level locations based on type
    if (typeId === 'RESORT') {
      // For resorts, show only resort areas
      const response = await fetch('/api/locations?type=resort-areas')
      const data = await response.json()
      setCurrentLocations(data.locations || [])
    } else if (typeId === 'RESTAURANT' || typeId === 'ENTERTAINMENT') {
      // Restaurants and entertainment can be in parks OR resorts, show both
      const response = await fetch('/api/locations')
      const data = await response.json()
      setCurrentLocations(data.locations || [])
    } else {
      // Attractions and shows are primarily in parks
      const response = await fetch('/api/locations?type=parks')
      const data = await response.json()
      setCurrentLocations(data.locations || [])
    }
  }

  const handleLocationSelect = async (location: LocationOption) => {
    const newPath = [...locationPath, { id: location.id, name: location.name }]
    setLocationPath(newPath)

    if (location.hasChildren) {
      // Fetch and show children
      const children = await fetchChildren(location.id)
      if (children.length > 0) {
        setCurrentLocations(children)
        setShowingResults(false)
      } else {
        // No children, show results
        setShowingResults(true)
      }
    } else {
      // No children, show results
      setShowingResults(true)
    }
  }

  const handleBack = async () => {
    if (locationPath.length > 1) {
      // Go back one level
      const newPath = locationPath.slice(0, -1)
      setLocationPath(newPath)
      const parentId = newPath[newPath.length - 1].id
      const children = await fetchChildren(parentId)
      setCurrentLocations(children)
      setShowingResults(false)
    } else if (locationPath.length === 1) {
      // Go back to top level locations
      setLocationPath([])
      if (selectedType === 'RESORT') {
        const response = await fetch('/api/locations?type=resort-areas')
        const data = await response.json()
        setCurrentLocations(data.locations || [])
      } else if (selectedType === 'RESTAURANT' || selectedType === 'ENTERTAINMENT') {
        const response = await fetch('/api/locations')
        const data = await response.json()
        setCurrentLocations(data.locations || [])
      } else {
        const response = await fetch('/api/locations?type=parks')
        const data = await response.json()
        setCurrentLocations(data.locations || [])
      }
      setShowingResults(false)
    } else {
      // Go back to type selection
      setSelectedType(null)
      setCurrentLocations(initialLocations)
      setShowingResults(false)
    }
  }

  const clearAll = () => {
    setSelectedType(null)
    setLocationPath([])
    setCurrentLocations(initialLocations)
    setShowingResults(false)
  }

  const viewResults = () => {
    setShowingResults(true)
  }

  const getSelectedTypeName = () => ENTITY_TYPES.find(t => t.id === selectedType)?.name || ''

  // Navigate to a specific level in the breadcrumb
  const navigateToLevel = async (level: number) => {
    if (level === 0) {
      // Go back to type selection (clear everything)
      clearAll()
    } else if (level === 1) {
      // Go back to first location level (just type selected)
      setLocationPath([])
      setShowingResults(false)
      if (selectedType === 'RESORT') {
        const response = await fetch('/api/locations?type=resort-areas')
        const data = await response.json()
        setCurrentLocations(data.locations || [])
      } else if (selectedType === 'RESTAURANT' || selectedType === 'ENTERTAINMENT') {
        const response = await fetch('/api/locations')
        const data = await response.json()
        setCurrentLocations(data.locations || [])
      } else {
        const response = await fetch('/api/locations?type=parks')
        const data = await response.json()
        setCurrentLocations(data.locations || [])
      }
    } else {
      // Go back to a specific location level
      const newPath = locationPath.slice(0, level - 1)
      setLocationPath(newPath)
      setShowingResults(false)
      if (newPath.length > 0) {
        const parentId = newPath[newPath.length - 1].id
        const children = await fetchChildren(parentId)
        setCurrentLocations(children)
      }
    }
  }

  // Render breadcrumb
  const renderBreadcrumb = () => {
    const crumbs: { label: string; level: number }[] = []
    if (selectedType) crumbs.push({ label: getSelectedTypeName(), level: 1 })
    locationPath.forEach((loc, i) => crumbs.push({ label: loc.name, level: i + 2 }))

    if (crumbs.length === 0) return null

    const isLastCrumb = (index: number) => index === crumbs.length - 1

    return (
      <div className="flex items-center gap-2 mb-4 text-sm flex-wrap">
        <span className="text-slate-500">Showing:</span>
        <div className="flex items-center gap-1 flex-wrap">
          {crumbs.map((crumb, i) => (
            <span key={i} className="flex items-center">
              <button
                onClick={() => !isLastCrumb(i) && navigateToLevel(crumb.level)}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  isLastCrumb(i)
                    ? 'bg-[#1E4289] text-white cursor-default'
                    : 'bg-[#1E4289]/70 text-white hover:bg-[#1E4289] cursor-pointer'
                }`}
                disabled={isLastCrumb(i)}
              >
                {crumb.label}
              </button>
              {i < crumbs.length - 1 && <BsChevronRight className="mx-1 text-slate-400" />}
            </span>
          ))}
        </div>
        <button
          onClick={clearAll}
          className="ml-2 p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
          title="Clear all filters"
        >
          <BsX className="w-4 h-4" />
        </button>
      </div>
    )
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 mb-8">
      <h2 className="text-lg font-semibold text-slate-800 mb-4">Find the Best...</h2>

      {renderBreadcrumb()}

      {/* Back button */}
      {(selectedType || locationPath.length > 0) && !showingResults && (
        <button
          onClick={handleBack}
          className="flex items-center gap-1 text-sm text-[#1E4289] hover:underline mb-4"
        >
          <BsArrowLeft /> Back
        </button>
      )}

      {/* Step 0: Select Type */}
      {!selectedType && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {ENTITY_TYPES.map((type) => {
            const Icon = type.icon
            return (
              <button
                key={type.id}
                onClick={() => handleTypeSelect(type.id)}
                className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all hover:border-[#1E4289] hover:bg-[#1E4289]/5 border-slate-200"
              >
                <Icon className="w-8 h-8 text-[#1E4289]" />
                <span className="text-sm font-medium text-slate-700">{type.name}</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Location Selection - arbitrary depth */}
      {selectedType && !showingResults && (
        <div>
          <p className="text-sm text-slate-600 mb-4">
            {locationPath.length === 0 ? (
              <>
                Select a location to narrow down your search, or{' '}
                <button onClick={viewResults} className="text-[#1E4289] hover:underline font-medium">
                  view all {getSelectedTypeName().toLowerCase()}
                </button>
              </>
            ) : (
              <>
                Select a more specific area, or{' '}
                <button onClick={viewResults} className="text-[#1E4289] hover:underline font-medium">
                  view results in {locationPath[locationPath.length - 1].name}
                </button>
              </>
            )}
          </p>

          {loadingLocations ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#1E4289] border-t-transparent"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {currentLocations.map((location) => (
                <button
                  key={location.id}
                  onClick={() => handleLocationSelect(location)}
                  className="flex items-center justify-between p-4 rounded-lg border-2 border-slate-200 transition-all hover:border-[#1E4289] hover:bg-[#1E4289]/5 text-left"
                >
                  <span className="font-medium text-slate-700">{location.name}</span>
                  {location.hasChildren && <BsChevronRight className="text-slate-400" />}
                </button>
              ))}
            </div>
          )}

          {!loadingLocations && currentLocations.length === 0 && (
            <div className="text-center py-4">
              <p className="text-slate-500">No sub-areas available.</p>
              <button onClick={viewResults} className="mt-2 text-[#1E4289] hover:underline font-medium">
                View results
              </button>
            </div>
          )}
        </div>
      )}

      {/* Results mode indicator */}
      {showingResults && (
        <div className="text-center py-4">
          <p className="text-slate-600">Showing results below</p>
        </div>
      )}
    </div>
  )
}
